import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { BRANCH_META, BRANCH_ORDER } from "@/lib/branches";
import HorizonRing from "@/components/HorizonRing";
import { Award, Compass, ScrollText, ArrowLeft, Share2, Crown, MapPin, Globe, Star, Sparkles } from "lucide-react";
import { motion } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicProfile() {
    const { username } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        api
            .get(`/public/profile/${username}`)
            .then((r) => setData(r.data))
            .catch(() => setError("This Explorer hasn't been discovered yet."));
        const og = `${BACKEND_URL}/api/public/og/${username}`;
        const meta = document.createElement("meta");
        meta.setAttribute("property", "og:image");
        meta.setAttribute("content", og);
        document.head.appendChild(meta);
        const twMeta = document.createElement("meta");
        twMeta.setAttribute("name", "twitter:image");
        twMeta.setAttribute("content", og);
        document.head.appendChild(twMeta);
        return () => {
            meta.remove();
            twMeta.remove();
        };
    }, [username]);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
        } catch {}
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    if (error)
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6">
                <p className="chip" style={{ background: BRANCH_META.sciences.soft, color: BRANCH_META.sciences.text }}>
                    404
                </p>
                <h1 className="font-display font-black text-4xl tracking-tighter text-slate-900">
                    {error}
                </h1>
                <Link to="/" className="arcade-btn" data-variant="primary">
                    Back to Atlas <Compass className="w-4 h-4" />
                </Link>
            </div>
        );

    if (!data)
        return (
            <div className="min-h-screen flex items-center justify-center text-slate-500 font-display">
                Loading ledger…
            </div>
        );

    const { user, beacons, ledger, featured_contributions } = data;
    const initials = (user.name || "?")
        .split(" ")
        .map((s) => s[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();
    const masters = user.master_branches || [];
    const isMaster = masters.length > 0;
    const primary = masters[0] || Object.entries(user.branch_xp || {}).sort((a, b) => b[1] - a[1])[0]?.[0] || "arts";
    const meta = BRANCH_META[primary];
    const masterGradient = isMaster
        ? `conic-gradient(from 0deg, ${masters.map((m) => BRANCH_META[m]?.color).join(", ")}, ${BRANCH_META[masters[0]]?.color})`
        : null;

    return (
        <div className="min-h-screen px-4 sm:px-8 py-12">
            <div className="max-w-4xl mx-auto">
                <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900"
                    data-testid="back-to-landing"
                >
                    <ArrowLeft className="w-4 h-4" /> Project Atlas
                </Link>

                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`crystal rounded-3xl p-8 mt-4 relative overflow-hidden ${isMaster ? "master-hero" : ""}`}
                    style={{
                        background: isMaster
                            ? `radial-gradient(circle at 90% 10%, ${meta.soft} 0%, #fff 60%), radial-gradient(circle at 10% 90%, ${BRANCH_META[masters[masters.length - 1]].soft}88 0%, transparent 40%)`
                            : `radial-gradient(circle at 90% 10%, ${meta.soft} 0%, #fff 60%)`,
                        boxShadow: isMaster ? `0 30px 80px -30px ${meta.color}55, 0 0 0 2px ${meta.color}22 inset` : undefined,
                    }}
                    data-testid={isMaster ? "master-hero" : "explorer-hero"}
                >
                    {isMaster && (
                        <>
                            {/* Master aura glows */}
                            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-30 blur-3xl pointer-events-none" style={{ background: meta.color }} />
                            <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full opacity-20 blur-3xl pointer-events-none" style={{ background: BRANCH_META[masters[masters.length - 1]].color }} />
                            {/* Sparkles corner ornament */}
                            <div className="absolute top-4 right-4 text-slate-400 opacity-40" data-testid="master-ornament">
                                <Sparkles className="w-6 h-6" />
                            </div>
                        </>
                    )}
                    <div className="flex flex-col sm:flex-row items-start gap-6 relative">
                        <div className="relative flex-shrink-0" data-testid="public-avatar">
                            {isMaster && (
                                <motion.div
                                    className="absolute inset-0 rounded-2xl -m-1.5"
                                    style={{ background: masterGradient, filter: "blur(2px)" }}
                                    animate={{ rotate: 360 }}
                                    transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
                                />
                            )}
                            <div
                                className="w-24 h-24 rounded-2xl flex items-center justify-center font-display font-black text-4xl text-white shadow-lg relative"
                                style={{
                                    background: `linear-gradient(135deg, ${meta.color}, ${BRANCH_META.humanities.color})`,
                                    boxShadow: `0 12px 40px -8px ${meta.color}`,
                                }}
                            >
                                {initials}
                                {isMaster && (
                                    <Crown
                                        className="absolute -top-3 -right-3 w-9 h-9 rotate-12 drop-shadow-lg"
                                        color="#FFC107"
                                        fill="#FFC107"
                                        data-testid="public-master-crown"
                                    />
                                )}
                            </div>
                        </div>
                        <div className="flex-1 min-w-0">
                            {isMaster ? (
                                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-white text-xs font-display font-black tracking-widest uppercase shadow-md" style={{ background: `linear-gradient(90deg, ${masters.map((m) => BRANCH_META[m]?.color).join(", ")})` }} data-testid="master-title-chip">
                                    <Crown className="w-3.5 h-3.5" fill="#FFC107" color="#FFC107" />
                                    Discipline Master · {masters.map((m) => BRANCH_META[m]?.name).join(" · ")}
                                </div>
                            ) : (
                                <div className="chip" style={{ background: meta.soft, color: meta.text }}>
                                    Verified Explorer · Level {user.level}
                                </div>
                            )}
                            <h1
                                className="font-display font-black text-5xl tracking-tighter text-slate-900 mt-2 leading-none"
                                data-testid="public-name"
                            >
                                {user.name}
                            </h1>
                            <p
                                className="font-mono text-sm mt-1"
                                style={{ color: meta.color }}
                                data-testid="public-handle"
                            >
                                @{user.username}
                            </p>
                            {user.bio && (
                                <p className="text-sm text-slate-700 mt-2 max-w-lg" data-testid="public-bio">
                                    {user.bio}
                                </p>
                            )}
                            <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-500">
                                {user.location && (
                                    <span className="inline-flex items-center gap-1">
                                        <MapPin className="w-3 h-3" /> {user.location}
                                    </span>
                                )}
                                {user.website && (
                                    <a
                                        href={user.website.startsWith("http") ? user.website : `https://${user.website}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 hover:text-humanities"
                                        data-testid="public-website"
                                    >
                                        <Globe className="w-3 h-3" /> {user.website.replace(/^https?:\/\//, "")}
                                    </a>
                                )}
                            </div>
                            <div className="text-sm text-slate-600 mt-3">
                                <span className="font-display font-bold">{user.xp}</span> XP ·{" "}
                                <span className="font-display font-bold">{beacons.length}</span> beacon
                                {beacons.length !== 1 ? "s" : ""} ·{" "}
                                <span className="font-display font-bold">{user.badges.length}</span> merit badges
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <Link
                                to={`/u/${user.username}/resume`}
                                className="arcade-btn"
                                data-variant="primary"
                                data-testid="view-resume"
                            >
                                <ScrollText className="w-4 h-4" /> View resume
                            </Link>
                            <button
                                onClick={copy}
                                className="arcade-btn"
                                data-variant="ghost"
                                data-testid="copy-share-link"
                            >
                                <Share2 className="w-4 h-4" /> {copied ? "Copied!" : "Copy link"}
                            </button>
                        </div>
                    </div>

                    <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {BRANCH_ORDER.map((b) => {
                            const xp = (user.branch_xp || {})[b] || 0;
                            const m = BRANCH_META[b];
                            const isThisMastered = masters.includes(b);
                            return (
                                <div
                                    key={b}
                                    className={`rounded-2xl p-3 flex items-center gap-3 ${isThisMastered ? "ring-2" : ""}`}
                                    style={{ background: m.soft, boxShadow: isThisMastered ? `inset 0 0 0 2px ${m.color}` : undefined }}
                                    data-testid={`branch-tile-${b}`}
                                >
                                    <HorizonRing size={54} stroke={7} pct={Math.min(100, (xp / 250) * 100)} color={m.color} />
                                    <div>
                                        <div className="text-xs uppercase tracking-widest font-bold flex items-center gap-1" style={{ color: m.text }}>
                                            {m.name}
                                            {isThisMastered && <Crown className="w-3 h-3" fill={m.color} color={m.color} />}
                                        </div>
                                        <div className="font-display font-black text-xl tracking-tighter text-slate-900">
                                            {xp}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </motion.div>

                {/* Featured Contributions — Masters get a dedicated highlight section */}
                {isMaster && featured_contributions && featured_contributions.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.05 }}
                        className="mt-6 crystal rounded-3xl p-6 relative overflow-hidden"
                        style={{ background: `linear-gradient(135deg, ${meta.soft} 0%, #fff 60%)` }}
                        data-testid="featured-contributions"
                    >
                        <div className="flex items-center gap-2">
                            <Star className="w-5 h-5" fill={meta.color} color={meta.color} />
                            <h2 className="font-display font-black text-2xl tracking-tight text-slate-900">
                                Featured contributions
                            </h2>
                        </div>
                        <p className="text-sm text-slate-600 mt-1">
                            Signature work from a verified Discipline Master.
                        </p>
                        <div className="mt-4 space-y-2">
                            {featured_contributions.map((f, i) => (
                                <div key={i} className="rounded-2xl border border-slate-200 bg-white/80 p-4 flex items-start gap-3" data-testid={`featured-${i}`}>
                                    <div
                                        className="w-9 h-9 rounded-xl flex-shrink-0 flex items-center justify-center text-white font-display font-black"
                                        style={{ background: meta.color }}
                                    >
                                        {i + 1}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded" style={{ background: meta.soft, color: meta.text }}>
                                                {f.event_type}
                                            </span>
                                            {f.discipline_slug && (
                                                <span className="text-xs text-slate-500 font-mono">
                                                    {f.discipline_slug.replace(/-/g, " ")}
                                                </span>
                                            )}
                                            <span className="ml-auto text-xs text-slate-400">
                                                {new Date(f.created_at).toLocaleDateString(undefined, { month: "short", year: "numeric" })}
                                            </span>
                                        </div>
                                        <p className="text-sm text-slate-800 mt-1">{f.message}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                )}

                <div className="mt-6 crystal rounded-3xl p-6">
                    <div className="flex items-center gap-2">
                        <Award className="w-4 h-4" color={BRANCH_META.arts.color} />
                        <h2 className="font-display font-bold text-xl tracking-tight text-slate-900">
                            Merit badges
                        </h2>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                        {user.badges.length === 0 && (
                            <p className="text-sm text-slate-500">No badges yet.</p>
                        )}
                        {user.badges.map((b, i) => {
                            const m = BRANCH_META[b.branch] || BRANCH_META.arts;
                            return (
                                <div
                                    key={i}
                                    className="chip"
                                    style={{ background: m.soft, color: m.text, border: `1.5px solid ${m.color}` }}
                                >
                                    <Award className="w-3 h-3" /> {b.name}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {ledger.length > 0 && (
                    <div className="mt-6 crystal rounded-3xl p-6">
                        <div className="flex items-center gap-2">
                            <ScrollText className="w-4 h-4" color={BRANCH_META.humanities.color} />
                            <h2 className="font-display font-bold text-xl tracking-tight text-slate-900">
                                Public ledger
                            </h2>
                        </div>
                        <div className="mt-3 space-y-2 max-h-72 overflow-y-auto no-scrollbar">
                            {ledger.map((e, i) => (
                                <div key={i} className="ledger-row text-slate-700">
                                    <span className="text-humanities font-bold uppercase">
                                        {String(e.event_type).padEnd(8, " ")}
                                    </span>{" "}
                                    <span className="text-slate-400">
                                        {new Date(e.created_at).toLocaleDateString()}
                                    </span>{" "}
                                    <span>{e.message}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="mt-8 text-center">
                    <p className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-2">
                        Verified on Project Atlas
                    </p>
                    <Link to="/" className="arcade-btn" data-variant="primary" data-testid="public-cta-join">
                        Claim your own ledger <Compass className="w-4 h-4" />
                    </Link>
                </div>
            </div>
        </div>
    );
}
